import React, { useState } from 'react'
import { 
  Settings as SettingsIcon, 
  User, 
  Shield, 
  Bell, 
  Palette, 
  Mail, 
  Building, 
  Key, 
  CreditCard, 
  LogOut, 
  Trash2,
  Copy,
  Check,
  MessageCircle
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useStartupWizard } from '@/contexts/StartupWizardContext'
import { useUserService } from '@/hooks/useUserService'
import { useFirebaseEmail } from '@/hooks/useFirebaseEmail'
import { toast } from 'react-hot-toast'
import LoadingSpinner from '@components/UI/LoadingSpinner'
import Config from '@/utils/Config'
import useGoogleAnalytics from '@/hooks/useGoogleAnalytics'
import { isDemoUser, isTestUser } from '@/utils/adminUtils'

const Settings = () => {
  const { currentUser, currentUserData, logout, resetPassword } = useAuth()
  const { 
    deleteUser: deleteUserService, 
    updateUser,
    getUserEmailSubscription,
    subscribeUserToEmails,
    unsubscribeUserFromEmails
  } = useUserService()
  const { sendAdminEmail, loading: emailLoading, error: emailError } = useFirebaseEmail()
  const [copiedField, setCopiedField] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showEmailSubscriptionConfirm, setShowEmailSubscriptionConfirm] = useState(false)
  const [editingField, setEditingField] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [isUpdatingEmailSubscription, setIsUpdatingEmailSubscription] = useState(false)
  const [emailSubscriptionStatus, setEmailSubscriptionStatus] = useState(null)
  const [showContactSupport, setShowContactSupport] = useState(false)
  const [contactSubject, setContactSubject] = useState('')
  const [contactMessage, setContactMessage] = useState('')
  
  // Google Analytics tracking
  const { trackSettingsChange } = useGoogleAnalytics()

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      toast.success(`${field} copied to clipboard!`)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleStartEdit = (field, currentValue) => {
    setEditingField(field)
    setEditValue(currentValue)
  }

  const handleSaveEdit = async (field) => {
    if (!currentUser?.uid) {
      toast.error('User information not available')
      return
    }

    try {
      await updateUser(currentUser.uid, { [field]: editValue })
      toast.success(`${field} updated successfully`)
      setEditingField(null)
      setEditValue('')
    } catch (error) {
      console.error('Update user error:', error)
      toast.error(error.message || 'Failed to update user information')
    }
  }

  const handleCancelEdit = () => {
    setEditingField(null)
    setEditValue('')
  }

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('Logged out successfully')
      setShowLogoutConfirm(false)
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Failed to logout')
    }
  }

  const handleDeleteUser = async () => {
    if (!currentUser?.uid) {
      toast.error('User information not available for deletion')
      setShowDeleteConfirm(false)
      return
    }

    setIsDeleting(true)
    try {
      await deleteUserService(currentUser.uid, currentUser.email, currentUserData?.displayName)
      toast.success('User account deleted successfully')
      setShowDeleteConfirm(false)
      // Reload the main page after successful deletion
      setTimeout(() => {
        window.location.reload()
      }, 1000) // Small delay to show the success message
    } catch (error) {
      console.error('Delete user error:', error)
      toast.error(error.message || 'Failed to delete user account. Please try again.')
      setShowDeleteConfirm(false)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleResetPassword = async () => {
    if (!currentUser?.email) {
      toast.error('No email address found for password reset')
      return
    }

    try {
      await resetPassword(currentUser.email)
      toast.success('Password reset email sent! Check your inbox.')
      setShowResetPasswordConfirm(false)
    } catch (error) {
      console.error('Password reset error:', error)
      toast.error('Failed to send password reset email. Please try again.')
    }
  }

  // Load email subscription status on component mount
  React.useEffect(() => {
    const loadEmailSubscriptionStatus = async () => {
      if (!currentUser?.uid) return
      
      try {
        const isSubscribed = await getUserEmailSubscription(currentUser.uid)
        setEmailSubscriptionStatus(isSubscribed)
      } catch (error) {
        console.error('Error loading email subscription status:', error)
        // Default to subscribed if we can't load the status
        setEmailSubscriptionStatus(true)
      }
    }

    loadEmailSubscriptionStatus()
  }, [currentUser?.uid, getUserEmailSubscription])

  const handleEmailSubscriptionToggle = async () => {
    if (!currentUser?.uid) {
      toast.error('User information not available')
      return
    }

    setIsUpdatingEmailSubscription(true)
    try {
      if (emailSubscriptionStatus) {
        // Currently subscribed, so unsubscribe
        await unsubscribeUserFromEmails(currentUser.uid)
        setEmailSubscriptionStatus(false)
        toast.success('Successfully unsubscribed from email notifications')
        
        // Track settings change
        trackSettingsChange('email_subscription', 'unsubscribed')
      } else {
        // Currently unsubscribed, so subscribe
        await subscribeUserToEmails(currentUser.uid)
        setEmailSubscriptionStatus(true)
        toast.success('Successfully subscribed to email notifications')
        
        // Track settings change
        trackSettingsChange('email_subscription', 'subscribed')
      }
      setShowEmailSubscriptionConfirm(false)
    } catch (error) {
      console.error('Email subscription toggle error:', error)
      toast.error(error.message || 'Failed to update email subscription status')
    } finally {
      setIsUpdatingEmailSubscription(false)
    }
  }

  const handleContactSupport = async () => {
    if (!contactSubject.trim()) {
      toast.error('Please enter a subject')
      return
    }
    
    if (!contactMessage.trim()) {
      toast.error('Please enter a message')
      return
    }

    try {
      const userDetails = {
        displayName: currentUserData?.displayName || 'N/A',
        username: currentUserData?.username || 'N/A',
        uid: currentUser?.uid || 'N/A',
        subscriptionStatus: currentUserData?.subscriptionStatus || 'N/A',
        trialEndDate: currentUserData?.trialEndDate ? new Date(currentUserData.trialEndDate).toLocaleDateString() : 'N/A'
      }

      const subject = `Support Request: ${contactSubject}`
      const fromField = `${userDetails.displayName} <${userDetails.username}>`
      
      const text = `Support request from ${userDetails.displayName} (${userDetails.username}):\n\n${contactMessage}\n\n--- USER DETAILS ---\nDisplay Name: ${userDetails.displayName}\nUsername: ${userDetails.username}\nUser ID: ${userDetails.uid}\nSubscription Status: ${userDetails.subscriptionStatus}\nTrial End Date: ${userDetails.trialEndDate}`
      
      const html = {
        title: 'Support Request',
        text: `
          <p><strong>From:</strong> ${fromField}</p>
          <p><strong>Subject:</strong> ${contactSubject}</p>
          <p><strong>Message:</strong></p>
          <p>${contactMessage.replace(/\n/g, '<br>')}</p>
          
          <hr style="margin: 20px 0; border: 1px solid #e5e7eb;">
          
          <h3 style="color: #374151; margin-bottom: 10px;">📋 User Details</h3>
          <table style="width: 100%; border-collapse: collapse; background-color: #f9fafb; border-radius: 8px; padding: 15px;">
            <tr>
              <td style="padding: 8px; font-weight: bold; color: #374151;">Display Name:</td>
              <td style="padding: 8px; color: #6b7280;">${userDetails.displayName}</td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold; color: #374151;">Username:</td>
              <td style="padding: 8px; color: #6b7280;">${userDetails.username}</td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold; color: #374151;">User ID:</td>
              <td style="padding: 8px; color: #6b7280; font-family: monospace;">${userDetails.uid}</td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold; color: #374151;">Subscription Status:</td>
              <td style="padding: 8px; color: #6b7280;">${userDetails.subscriptionStatus}</td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold; color: #374151;">Trial End Date:</td>
              <td style="padding: 8px; color: #6b7280;">${userDetails.trialEndDate}</td>
            </tr>
          </table>
        `
      }

      await sendAdminEmail(subject, text, html)
      toast.success('Support request sent successfully!')
      setShowContactSupport(false)
      setContactSubject('')
      setContactMessage('')
    } catch (error) {
      console.error('Contact support error:', error)
      toast.error('Failed to send support request')
    }
  }

  const userInfo = [
    {
      label: 'Business Name',
      value: currentUserData?.displayName || 'Not set',
      icon: Building,
      copyable: true,
      editable: true,
      field: 'displayName'
    },
    {
      label: 'Email',
      value: currentUser?.email || 'Not available',
      icon: Mail,
      copyable: true,
      editable: false
    },
    {
      label: 'User ID',
      value: currentUser?.uid || 'Not available',
      icon: User,
      copyable: true,
      editable: false
    }
  ]

  const actionButtons = [
    {
      label: 'Reset Password',
      icon: Key,
      onClick: () => setShowResetPasswordConfirm(true),
      color: 'bg-blue-500 hover:bg-blue-600',
      textColor: 'text-white'
    },
    {
      label: emailSubscriptionStatus ? 'Unsubscribe from Emails' : 'Subscribe to Emails',
      icon: Mail,
      onClick: () => setShowEmailSubscriptionConfirm(true),
      color: emailSubscriptionStatus ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600',
      textColor: 'text-white',
      disabled: emailSubscriptionStatus === null
    },
    // Only show Manage Subscriptions button for non-demo and non-test users
    ...((isDemoUser(currentUserData) || isTestUser(currentUserData)) ? [] : [{
      label: 'Manage Subscriptions',
      icon: CreditCard,
      onClick: () => {
        window.open(Config.stripeSubscriptionsPage, '_blank')
      },
      color: 'bg-green-500 hover:bg-green-600',
      textColor: 'text-white'
    }]),
    {
      label: 'Logout',
      icon: LogOut,
      onClick: () => setShowLogoutConfirm(true),
      color: 'bg-yellow-500 hover:bg-yellow-600',
      textColor: 'text-white'
    },
    {
      label: 'Delete User',
      icon: Trash2,
      onClick: () => setShowDeleteConfirm(true),
      color: 'bg-red-500 hover:bg-red-600',
      textColor: 'text-white'
    },
    {
      label: 'Contact Support',
      icon: MessageCircle,
      onClick: () => setShowContactSupport(true),
      color: 'bg-purple-500 hover:bg-purple-600',
      textColor: 'text-white'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage your account and system preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
          </div>
          
          <div className="space-y-4">
            {userInfo.map((info, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3 flex-1">
                  <info.icon className="w-4 h-4 text-gray-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">{info.label}</p>
                    {editingField === info.field ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="text-sm text-gray-900 font-mono bg-white border border-gray-300 rounded px-2 py-1 flex-1"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveEdit(info.field)}
                          className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-900 font-mono">{info.value}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {info.editable && editingField !== info.field && (
                    <button
                      onClick={() => handleStartEdit(info.field, info.value)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={`Edit ${info.label}`}
                    >
                      <SettingsIcon className="w-4 h-4" />
                    </button>
                  )}
                  {info.copyable && (
                    <button
                      onClick={() => copyToClipboard(info.value, info.label)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={`Copy ${info.label}`}
                    >
                      {copiedField === info.label ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <SettingsIcon className="w-5 h-5 text-yellow-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Account Actions</h2>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {actionButtons.map((button, index) => (
              <button
                key={index}
                onClick={button.onClick}
                disabled={button.disabled}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${button.color} ${button.textColor} ${
                  button.disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <button.icon className="w-4 h-4" />
                <span>{button.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 border border-red-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-red-100 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Delete Account</h3>
                <p className="text-sm text-gray-500">This action is permanent and cannot be undone</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-red-800 mb-2">⚠️ Warning: This will permanently delete:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Your user account and authentication</li>
                <li>• All your screens and content</li>
                <li>• All your schedules and device configurations</li>
                <li>• All your subscription data</li>
                <li>• All your uploaded files and media</li>
              </ul>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you absolutely sure you want to delete your account? This action cannot be reversed and you will lose access to all your data immediately.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={isDeleting}
                className="flex-1 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Deleting Account...
                  </>
                ) : (
                  'Yes, Delete My Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetPasswordConfirm && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Key className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to reset your password? A password reset email will be sent to <strong>{currentUser?.email}</strong>.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetPasswordConfirm(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Send Reset Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <LogOut className="w-5 h-5 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Logout</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to logout? You will be signed out of your account and redirected to the login page.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Subscription Confirmation Modal */}
      {showEmailSubscriptionConfirm && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${
                emailSubscriptionStatus ? 'bg-orange-100' : 'bg-green-100'
              }`}>
                <Mail className={`w-5 h-5 ${
                  emailSubscriptionStatus ? 'text-orange-600' : 'text-green-600'
                }`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {emailSubscriptionStatus ? 'Unsubscribe from Emails' : 'Subscribe to Emails'}
              </h3>
            </div>
            
            <div className={`mb-6 p-4 rounded-lg ${
              emailSubscriptionStatus ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'
            }`}>
              <h4 className={`font-semibold mb-2 ${
                emailSubscriptionStatus ? 'text-orange-800' : 'text-green-800'
              }`}>
                {emailSubscriptionStatus ? '⚠️ Unsubscribing from emails will:' : '✅ Subscribing to emails will:'}
              </h4>
              <ul className={`text-sm space-y-1 ${
                emailSubscriptionStatus ? 'text-orange-700' : 'text-green-700'
              }`}>
                {emailSubscriptionStatus ? (
                  <>
                    <li>• Stop receiving password reset emails</li>
                    <li>• Stop receiving account notifications</li>
                    <li>• Stop receiving system updates</li>
                    <li>• You can always resubscribe later</li>
                  </>
                ) : (
                  <>
                    <li>• Receive password reset emails</li>
                    <li>• Receive account notifications</li>
                    <li>• Receive system updates</li>
                    <li>• You can unsubscribe anytime</li>
                  </>
                )}
              </ul>
            </div>
            
            <p className="text-gray-600 mb-6">
              {emailSubscriptionStatus 
                ? 'Are you sure you want to unsubscribe from email notifications? You will no longer receive important account updates via email.'
                : 'Are you sure you want to subscribe to email notifications? You will receive important account updates and notifications via email.'
              }
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowEmailSubscriptionConfirm(false)}
                disabled={isUpdatingEmailSubscription}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleEmailSubscriptionToggle}
                disabled={isUpdatingEmailSubscription}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                  emailSubscriptionStatus ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {isUpdatingEmailSubscription ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    {emailSubscriptionStatus ? 'Unsubscribing...' : 'Subscribing...'}
                  </>
                ) : (
                  emailSubscriptionStatus ? 'Yes, Unsubscribe' : 'Yes, Subscribe'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Support Modal */}
      {showContactSupport && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] overflow-hidden" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 border border-purple-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-purple-100 rounded-full">
                <MessageCircle className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Contact Support</h3>
                <p className="text-sm text-gray-500">Send a message to our support team</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={contactSubject}
                  onChange={(e) => setContactSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  maxLength={100}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Describe your issue or question in detail..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  maxLength={1000}
                />
              </div>
              
              {emailError && (
                <div className="text-red-500 text-sm">
                  {emailError}
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowContactSupport(false)
                  setContactSubject('')
                  setContactMessage('')
                }}
                className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleContactSupport}
                disabled={emailLoading}
                className="flex-1 px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {emailLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Settings 